/**
 * Client for the scorecard-photo endpoints (/api/rounds/:id/scorecards,
 * /api/scorecard-uploads/…) and the shapes they return.
 */

const API = process.env.REACT_APP_API_URL;

export type GetToken = () => Promise<string>;

export type UploadStatus = 'reading' | 'needs_review' | 'ready' | 'agent_working' | 'committed' | 'discarded' | 'failed';

export interface Flag {
  id: string;
  type: string;
  severity: 'blocking' | 'warning' | 'info';
  ackable: boolean;
  acked: boolean;
  blocking: boolean;
  message: string;
  row_id?: string;
  hole?: number;
  holes?: number[];
}

export interface ViewRow {
  row_id: string;
  label: string;
  eligible: boolean;
  in_round: boolean;
  strokes: (number | null)[];
  putts: (number | null)[];
  penalties: (number | null)[];
  out: number | null;
  in: number | null;
  total: number | null;
  nines: Record<string, { mode: string; sum: number | null; target: number | null; balanced: boolean | null; suspects: number[]; unverified?: number[] }>;
  resolved_holes: number[];
}

export interface DraftCell {
  hole: number;
  raw: string | null;
  kind: string;
  value: number | null;
  confidence: number;
  source: 'read' | 'reviewer' | 'agent';
  marks: string[];
}

export interface Candidate { player_id: number; name: string; score: number; in_round: boolean; reasons: string[] }

export interface DraftRow {
  row_id: string;
  name_text: string | null;
  initials: string | null;
  cells: DraftCell[];
  written: Record<'out' | 'in' | 'total', { value: number; kind: string } | null>;
  assignment: { player_id: number | null; status: 'auto' | 'suggested' | 'unmatched' | 'confirmed' | 'skipped'; candidates: Candidate[]; remember_alias?: boolean };
  replace_existing: boolean;
  update_gross: boolean;
}

export interface Draft {
  played_holes: number[];
  pars: (number | null)[];
  include_putts: boolean;
  include_penalties: boolean;
  course_label: string | null;
  summary: string | null;
  rows: DraftRow[];
}

export interface View {
  rows: ViewRow[];
  flags: Flag[];
  blocking_count: number;
  eligible_count: number;
  can_commit: boolean;
}

export interface ChatMessage {
  message_id: number;
  role: 'user' | 'assistant';
  kind: 'chat' | 'assistant' | 'edit_event' | 'system';
  display_text: string;
  created_at: string;
}

/** A read in progress (updated every ~30s by the server) — see services/agentKit.js traces. */
export interface ReadProgress {
  phase: 'waiting' | 'thinking' | 'writing' | 'tool' | 'done';
  elapsed_ms: number;
  first_event_ms?: number | null;
  text_chars?: number;
  thinking_chars?: number;
  holes_written?: number;
  /** When the last data arrived from Claude (ms into the read), and time the server was asleep. */
  last_event_ms?: number | null;
  paused_ms?: number;
  fallbacks?: number;
  /** 2 when the card is being read twice (the progress is the read that is further behind). */
  passes?: number;
  request_id?: string | null;
  cap_ms?: number | null;
  at: string;
}

export interface ReadUsage {
  progress?: ReadProgress;
  summary?: string | null;
  total_ms?: number | null;
  failed?: boolean;
}

export interface ReviewState {
  upload: {
    upload_id: number;
    round_id: number;
    status: UploadStatus;
    error: string | null;
    created_at: string;
    committed_at: string | null;
    uploaded_by: string;
    /** The photo as uploaded (updated when it's replaced with "New photo"). */
    filename?: string | null;
    width?: number | null;
    height?: number | null;
    usage?: { read?: ReadUsage | null; photo?: { replaced_at?: string } | null } | null;
  };
  round: {
    round_id: number;
    date: string;
    course_name: string;
    teebox_name: string;
    number_of_holes: number;
    tournament_id: number | null;
    players: { player_id: number; first_name: string; last_name: string; player_score: number | null; has_holes: boolean }[];
  } | null;
  draft: Draft | null;
  view: View | null;
  messages: ChatMessage[];
  uploader_name: string | null;
  reviewer_name: string | null;
  can_review: boolean;
  players: { player_id: number; first_name: string; last_name: string }[];
}

export interface RoundScorecardSummary {
  players: number;
  with_holes: number;
  uploads: { upload_id: number; round_id: number; status: UploadStatus; reviewer_user_id: string; created_at: string }[];
}

export type DraftEdit =
  | { type: 'set_hole'; row_id: string; hole: number; strokes?: number | null; putts?: number | null; penalties?: number | null }
  | { type: 'set_written'; row_id: string; which: 'out' | 'in' | 'total'; value: number | null }
  | { type: 'assign'; row_id: string; player_id?: number | null; skip?: boolean; remember_alias?: boolean }
  | { type: 'row_options'; row_id: string; replace_existing?: boolean; update_gross?: boolean; notation_front?: string | null; notation_back?: string | null }
  | { type: 'ack'; flag_id: string; value?: boolean; note?: string }
  | { type: 'include'; putts?: boolean; penalties?: boolean };

export class ApiError extends Error {
  status: number;
  flags?: Flag[];
  constructor(status: number, message: string, flags?: Flag[]) {
    super(message);
    this.status = status;
    this.flags = flags;
  }
}

async function call<T>(getToken: GetToken, path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...(init.headers || {}) },
  });
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) throw new ApiError(res.status, body?.error || `Request failed (${res.status}).`, body?.flags);
  return body as T;
}

const json = (method: string, data?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: data === undefined ? undefined : JSON.stringify(data),
});

export const scorecardApi = {
  upload: (getToken: GetToken, roundId: number, image: Blob, meta: { filename?: string; width?: number; height?: number } = {}) => {
    const q = new URLSearchParams();
    if (meta.filename) q.set('filename', meta.filename);
    if (meta.width) q.set('width', String(meta.width));
    if (meta.height) q.set('height', String(meta.height));
    return call<{ upload_id: number }>(getToken, `/rounds/${roundId}/scorecards?${q.toString()}`, {
      method: 'POST',
      headers: { 'Content-Type': image.type || 'image/jpeg' },
      body: image,
    });
  },
  summaries: (getToken: GetToken, roundIds: number[]) =>
    call<Record<string, RoundScorecardSummary>>(getToken, `/scorecard-uploads/summary?round_ids=${roundIds.join(',')}`),
  get: (getToken: GetToken, uploadId: number) => call<ReviewState>(getToken, `/scorecard-uploads/${uploadId}`),
  edit: (getToken: GetToken, uploadId: number, edits: DraftEdit[]) =>
    call<{ view: View; draft: Draft; descriptions: string[] }>(getToken, `/scorecard-uploads/${uploadId}/draft`, json('PATCH', { edits })),
  chat: (getToken: GetToken, uploadId: number, text: string) =>
    call<{ status: UploadStatus }>(getToken, `/scorecard-uploads/${uploadId}/messages`, json('POST', { text })),
  commit: (getToken: GetToken, uploadId: number) =>
    call<{ saved: { player_id: number; name: string; total: number }[]; gross_updated: unknown[]; aliases_learned: unknown[] }>(getToken, `/scorecard-uploads/${uploadId}/commit`, json('POST', {})),
  discard: (getToken: GetToken, uploadId: number) => call<{ discarded: boolean }>(getToken, `/scorecard-uploads/${uploadId}/discard`, json('POST', {})),
  reread: (getToken: GetToken, uploadId: number, image?: Blob, meta: { filename?: string; width?: number; height?: number } = {}) => {
    const q = new URLSearchParams();
    if (meta.filename) q.set('filename', meta.filename);
    if (meta.width) q.set('width', String(meta.width));
    if (meta.height) q.set('height', String(meta.height));
    const qs = image && q.toString() ? `?${q.toString()}` : '';
    return call<{ status: UploadStatus }>(getToken, `/scorecard-uploads/${uploadId}/reread${qs}`, image
      ? { method: 'POST', headers: { 'Content-Type': image.type || 'image/jpeg' }, body: image }
      : json('POST', {}));
  },
  photoUrl: async (getToken: GetToken, uploadId: number): Promise<string> => {
    const token = await getToken();
    // no-cache: always ask the server (it answers 304 when unchanged), so an
    // older photo cached under this URL is never shown.
    const res = await fetch(`${API}/scorecard-uploads/${uploadId}/image`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-cache' });
    if (!res.ok) throw new ApiError(res.status, 'Photo not available.');
    return URL.createObjectURL(await res.blob());
  },
};

export const OPEN_STATUSES: UploadStatus[] = ['reading', 'needs_review', 'ready', 'agent_working'];

export function statusLabel(status: UploadStatus): string {
  switch (status) {
    case 'reading': return 'Reading the card…';
    case 'agent_working': return 'Assistant is checking…';
    case 'needs_review': return 'Needs review';
    case 'ready': return 'Ready to save';
    case 'committed': return 'Saved';
    case 'discarded': return 'Discarded';
    case 'failed': return "Couldn't read";
    default: return status;
  }
}
